// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './SettingsModal.css';
import SoundManager from '../utils/system/SoundManager';
import SoundEffects from '../utils/system/SoundEffects';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';

const SettingsModal = ({ show, onClose }) => {
  const [activeTab, setActiveTab] = useState('audio');
  const [settings, setSettings] = useState({
    shipVoice: 'matthew-ship-computer',
    soundPack: 'default',
    masterVolume: 80,
    difficulty: 'normal',
    purchaseConfirmThreshold: '500',
    warpSkipEnabled: 'true',
    autosaveInterval: '60000',
    crewDetailLevel: 'standard',
    crewActivityFrequency: 'normal',
    crewMinigamePrompts: true,
    crewNotifications: true
  });

  useEffect(() => {
    // Load settings from localStorage
    setSettings({
      shipVoice: localStorage.getItem(STORAGE_KEYS.SHIP_VOICE) || 'matthew-ship-computer',
      soundPack: localStorage.getItem(STORAGE_KEYS.SOUND_PACK) || 'default',
      masterVolume: parseInt(localStorage.getItem(STORAGE_KEYS.MASTER_VOLUME) || '80'),
      difficulty: localStorage.getItem(STORAGE_KEYS.DIFFICULTY) || 'normal',
      purchaseConfirmThreshold: localStorage.getItem(STORAGE_KEYS.PURCHASE_CONFIRM_THRESHOLD) || '500',
      warpSkipEnabled: localStorage.getItem(STORAGE_KEYS.WARP_SKIP_ENABLED) || 'true',
      autosaveInterval: localStorage.getItem(STORAGE_KEYS.AUTOSAVE_INTERVAL) || '60000',
      crewDetailLevel: localStorage.getItem(STORAGE_KEYS.CREW_DETAIL_LEVEL) || 'standard',
      crewActivityFrequency: localStorage.getItem(STORAGE_KEYS.CREW_ACTIVITY_FREQUENCY) || 'normal',
      crewMinigamePrompts: localStorage.getItem(STORAGE_KEYS.CREW_MINIGAME_PROMPTS) !== 'false',
      crewNotifications: localStorage.getItem(STORAGE_KEYS.CREW_NOTIFICATIONS) !== 'false'
    });
  }, [show]);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEYS.SHIP_VOICE, settings.shipVoice);
    localStorage.setItem(STORAGE_KEYS.SOUND_PACK, settings.soundPack);
    localStorage.setItem(STORAGE_KEYS.MASTER_VOLUME, settings.masterVolume);
    localStorage.setItem(STORAGE_KEYS.DIFFICULTY, settings.difficulty);
    localStorage.setItem(STORAGE_KEYS.PURCHASE_CONFIRM_THRESHOLD, settings.purchaseConfirmThreshold);
    localStorage.setItem(STORAGE_KEYS.WARP_SKIP_ENABLED, settings.warpSkipEnabled);
    localStorage.setItem(STORAGE_KEYS.AUTOSAVE_INTERVAL, settings.autosaveInterval);
    localStorage.setItem(STORAGE_KEYS.CREW_DETAIL_LEVEL, settings.crewDetailLevel);
    localStorage.setItem(STORAGE_KEYS.CREW_ACTIVITY_FREQUENCY, settings.crewActivityFrequency);
    localStorage.setItem(STORAGE_KEYS.CREW_MINIGAME_PROMPTS, settings.crewMinigamePrompts);
    localStorage.setItem(STORAGE_KEYS.CREW_NOTIFICATIONS, settings.crewNotifications);
    
    SoundManager.setVolume(settings.masterVolume / 100);
    SoundEffects.setVolume(settings.masterVolume / 100);
    
    onClose();
  };

  const handleReset = () => {
    setSettings({
      shipVoice: 'matthew-ship-computer',
      soundPack: 'default',
      masterVolume: 80,
      difficulty: 'normal',
      purchaseConfirmThreshold: '500',
      warpSkipEnabled: 'true',
      autosaveInterval: '60000',
      crewDetailLevel: 'standard',
      crewActivityFrequency: 'normal',
      crewMinigamePrompts: true,
      crewNotifications: true
    });
  };

  const previewVoice = () => {
    localStorage.setItem(STORAGE_KEYS.SHIP_VOICE, settings.shipVoice);
    SoundManager.play('warp_engaged');
  };

  const previewSound = () => {
    localStorage.setItem(STORAGE_KEYS.SOUND_PACK, settings.soundPack);
    SoundEffects.play('laser_fire');
  };

  if (!show) return null;

  return createPortal(
    <div className="settings-modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>⚙️ SETTINGS</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="settings-tabs">
          <button 
            className={activeTab === 'audio' ? 'active' : ''}
            onClick={() => setActiveTab('audio')}
          >
            🔊 Audio
          </button>
          <button 
            className={activeTab === 'gameplay' ? 'active' : ''}
            onClick={() => setActiveTab('gameplay')}
          >
            🎮 Gameplay
          </button>
          <button 
            className={activeTab === 'crew' ? 'active' : ''}
            onClick={() => setActiveTab('crew')}
          >
            👥 Crew Life
          </button>
        </div>

        <div className="settings-content">
          {activeTab === 'audio' && (
            <div className="settings-section">
              <div className="setting-item">
                <label>Ship Computer Voice</label>
                <div className="setting-control">
                  <select 
                    value={settings.shipVoice}
                    onChange={(e) => setSettings({...settings, shipVoice: e.target.value})}
                  >
                    <option value="matthew-ship-computer">Matthew - Professional</option>
                    <option value="matthew-glados">Matthew - GLaDOS Style</option>
                    <option value="joanna">Joanna - Friendly</option>
                    <option value="joanna-sultry">Joanna - Sultry</option>
                    <option value="danielle">Danielle - Casual</option>
                    <option value="danielle-bimbo">Danielle - Playful</option>
                    <option value="ruth-sultry">Ruth - Sultry</option>
                    <option value="stephen">Stephen - British</option>
                    <option value="80's-pop-culture">80's Pop Culture</option>
                    <option value="polly-neural">Polly - Neural AI</option>
                  </select>
                  <button className="preview-btn" onClick={previewVoice}>▶ Preview</button>
                </div>
              </div>

              <div className="setting-item">
                <label>Sound Effects Theme</label>
                <div className="setting-control">
                  <select 
                    value={settings.soundPack}
                    onChange={(e) => setSettings({...settings, soundPack: e.target.value})}
                  >
                    <option value="default">Default Sci-Fi</option>
                    <option value="doctor-who">Doctor Who</option>
                    <option value="star-trek">Star Trek</option>
                    <option value="star-wars">Star Wars</option>
                    <option value="cyberpunk">Cyberpunk</option>
                    <option value="retro-scifi">Retro Sci-Fi</option>
                    <option value="comedy">Comedy</option>
                  </select>
                  <button className="preview-btn" onClick={previewSound}>▶ Preview</button>
                </div>
              </div>

              <div className="setting-item">
                <label>Master Volume: {settings.masterVolume}%</label>
                <input 
                  type="range" 
                  min="0" 
                  max="100" 
                  value={settings.masterVolume}
                  onChange={(e) => setSettings({...settings, masterVolume: parseInt(e.target.value)})}
                />
              </div>
            </div>
          )}

          {activeTab === 'gameplay' && (
            <div className="settings-section">
              <div className="setting-item">
                <label>Difficulty</label>
                <select 
                  value={settings.difficulty}
                  onChange={(e) => setSettings({...settings, difficulty: e.target.value})}
                >
                  <option value="easy">Easy - Relaxed exploration</option>
                  <option value="normal">Normal - Balanced challenge</option>
                  <option value="hard">Hard - Survival mode</option>
                </select>
                <p className="setting-description">
                  {settings.difficulty === 'easy' && 'Less fuel use, less damage, cheaper repairs'}
                  {settings.difficulty === 'normal' && 'Standard fuel, damage, and repair costs'}
                  {settings.difficulty === 'hard' && 'More fuel use, more damage, expensive repairs'}
                </p>
              </div>

              <div className="setting-item">
                <label>Purchase Confirmations</label>
                <select
                  value={settings.purchaseConfirmThreshold}
                  onChange={(e) => setSettings({...settings, purchaseConfirmThreshold: e.target.value})}
                >
                  <option value="0">Off - Buy instantly</option>
                  <option value="500">500+ credits - Confirm expensive items</option>
                  <option value="1000">1000+ credits - Confirm very expensive items</option>
                  <option value="1">Always - Confirm every purchase</option>
                </select>
              </div>

              <div className="setting-item">
                <label>Warp Skip Button</label>
                <select
                  value={settings.warpSkipEnabled}
                  onChange={(e) => setSettings({...settings, warpSkipEnabled: e.target.value})}
                >
                  <option value="true">Show - Skip warp travel animation</option>
                  <option value="false">Hide - Full warp experience</option>
                </select>
              </div>

              <div className="setting-item">
                <label>Auto-Save Frequency</label>
                <select
                  value={settings.autosaveInterval}
                  onChange={(e) => setSettings({...settings, autosaveInterval: e.target.value})}
                >
                  <option value="30000">Every 30 seconds</option>
                  <option value="60000">Every 60 seconds</option>
                  <option value="120000">Every 2 minutes</option>
                  <option value="0">Off - Manual saves only</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'crew' && (
            <div className="settings-section">
              <div className="setting-item">
                <label>Detail Level</label>
                <select 
                  value={settings.crewDetailLevel}
                  onChange={(e) => setSettings({...settings, crewDetailLevel: e.target.value})}
                >
                  <option value="minimal">Minimal - Basic crew status only</option>
                  <option value="standard">Standard - Personalities and activities</option>
                  <option value="detailed">Detailed - Full relationships and events</option>
                  <option value="immersive">Immersive - Complete life simulation</option>
                </select>
              </div>

              <div className="setting-item">
                <label>Activity Frequency</label>
                <select 
                  value={settings.crewActivityFrequency}
                  onChange={(e) => setSettings({...settings, crewActivityFrequency: e.target.value})}
                >
                  <option value="rare">Rare - Few interruptions</option>
                  <option value="normal">Normal - Balanced activity</option>
                  <option value="frequent">Frequent - Active crew life</option>
                  <option value="constant">Constant - Always something happening</option>
                </select>
              </div>

              <div className="setting-item">
                <label className="checkbox-label">
                  <input 
                    type="checkbox"
                    checked={settings.crewMinigamePrompts}
                    onChange={(e) => setSettings({...settings, crewMinigamePrompts: e.target.checked})}
                  />
                  <span className="checkmark"></span>
                  Mini-game Prompts
                </label>
                <p className="setting-description">Show optional mini-games for crew activities</p>
              </div>

              <div className="setting-item">
                <label className="checkbox-label">
                  <input 
                    type="checkbox"
                    checked={settings.crewNotifications}
                    onChange={(e) => setSettings({...settings, crewNotifications: e.target.checked})}
                  />
                  <span className="checkmark"></span>
                  Crew Notifications
                </label>
                <p className="setting-description">Receive alerts about crew events and activities</p>
              </div>
            </div>
          )}
        </div>

        <div className="settings-footer">
          <button className="btn-secondary" onClick={handleReset}>Reset to Defaults</button>
          <div className="button-group">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" onClick={handleSave}>Save Settings</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SettingsModal;
